/**
 * Select: a native <select>, styled (cockatrice-ui `src/ui/Select.jsx`, ported to hyperscript; PLAN.md §0).
 * Native keeps phones, keyboards and screen readers happy.
 * options: [{ value, label, disabled }] or groups [{ group: "Ebook", options: [...] }], or pass <option> children.
 * onChange(value, event).
 */
import './css/select.css';
import { mergeProps, splitProps } from 'solid-js';
import { useFieldAttrs, wrapControl } from './Field.ts';
import Icon from './Icon.ts';
import { component, each, el, spread, when, whenSome } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';

/** One option. */
export interface SelectOption {
  readonly value: string;
  readonly label?: string | undefined;
  readonly disabled?: boolean | undefined;
}
/** A labelled group of options. */
export interface SelectGroup {
  readonly group: string;
  readonly options: readonly SelectOption[];
}

export interface SelectProps extends Attributes {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Options or groups. Or pass <option> children. */
  readonly options?: readonly (SelectOption | SelectGroup)[] | undefined;
  /** A disabled first option shown while the value is empty. */
  readonly placeholder?: string | undefined;
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called when the choice changes. */
  readonly onChange?: ((value: string, event: Event) => void) | undefined;
  /** 36 / 40 / 46 px tall. */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Fill the container (otherwise at least 200px). */
  readonly fullWidth?: boolean | undefined;
  /** Error styling without a message. */
  readonly invalid?: boolean | undefined;
  /** Disables the select. */
  readonly disabled?: boolean | undefined;
  /** Class on the wrapper. */
  readonly class?: string | undefined;
  /** <option> elements, when not using options. */
  readonly children?: Child;
}

const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass'] as const;

export default function Select(props: SelectProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () => component(SelectInput, rest));
}

const opt =
  (current: () => string | undefined) =>
  (o: SelectOption): Child =>
    el('option', { value: () => o.value, disabled: () => o.disabled, selected: () => current() === o.value }, o.label ?? o.value);

function SelectInput(props: SelectProps): Child {
  const p = mergeProps({ size: 'md' } as const, props);
  const [local, rest] = splitProps(p, [
    'id',
    'size',
    'options',
    'placeholder',
    'value',
    'defaultValue',
    'onChange',
    'error',
    'invalid',
    'required',
    'disabled',
    'class',
    'children',
    'fullWidth',
    'aria-describedby',
  ]);
  const f = useFieldAttrs(local);
  const current = (): string | undefined => local.value ?? local.defaultValue;
  const Opt = opt(current);

  return el(
    'div',
    {
      class: () =>
        ['ui-select', `ui-select--${local.size}`, local.fullWidth === true && 'ui-select--full', f.invalid() && 'is-invalid', f.disabled() && 'is-disabled', local.class]
          .filter(Boolean)
          .join(' '),
    },
    el(
      'select',
      spread(rest, {
        id: () => f.id(),
        class: 'ui-select__input',
        value: () => local.value ?? local.defaultValue,
        onChange: (event: On<Event, HTMLSelectElement>) => local.onChange?.(event.currentTarget.value, event),
        required: () => f.required(),
        disabled: () => f.disabled(),
        'aria-invalid': () => (f.invalid() ? 'true' : undefined),
        'aria-describedby': () => f.describedBy(),
      }),
      when(
        () => Boolean(local.placeholder),
        () => el('option', { value: '', disabled: true, selected: () => !current() }, () => local.placeholder),
      ),
      whenSome(
        () => local.options,
        (options) =>
          each(
            options,
            (o) => ('group' in o ? el('optgroup', { label: () => o.group }, each(() => o.options, Opt)) : Opt(o)),
          ),
        () => local.children,
      ),
    ),
    component(Icon, { name: 'chevronDown', size: 'sm', class: 'ui-select__chevron' }),
  );
}
